import React, { useState, useRef, useEffect } from "react";
import { Button, Form, Input, message, Switch, InputNumber } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import GenericDocumentManagerTable from "../../../../Components/Tables/GenericDocumentManagerTable";
import InputCard from "../../../../Components/Cards/InputCard";
import refuelIcon from "../../../../Assets/Icons/Container.svg";
import cardIcon from "../../../../Assets/Icons/credit_card.svg";
import resetIcon from "../../../../Assets/Icons/reset.svg";
import {
  createVehicle,
  getVehicles,
  getFuelTypes,
  getAvailableFuelCards,
  getVehicleDocumentTypes,
  getVehicleTypes,
  getNavixyApiKeys,
  getNavixyTrackers,
  getNavixyOdometer,
} from "../../../../API/FleetManager/Vehicles";
import { getDriversLookup } from "../../../../API/FleetManager/Drivers";

const toNavixyKeyOptions = (res) =>
  (res?.data?.data || [])
    .map((item) => {
      if (typeof item === "string") return { value: item, label: item };
      const value = item?.value ?? item?.api_key ?? item?.key;
      return value
        ? { value: String(value), label: String(item?.label ?? value) }
        : null;
    })
    .filter(Boolean);

const toNavixyTrackerOptions = (res) =>
  (res?.data?.data || [])
    .map((item) => {
      if (typeof item !== "object" || item === null) {
        return { value: String(item), label: String(item) };
      }
      const value = item.id ?? item.tracker_id ?? item.value;
      if (value === undefined || value === null) return null;
      const name = item.label ?? item.name;
      return {
        value: String(value),
        label: name ? `${name} (${value})` : String(value),
      };
    })
    .filter(Boolean);

const getErrorMessage = (data, fallback) => {
  if (!data) return fallback;
  if (data.message) return data.message;
  if (data.detail) return data.detail;

  const fieldErrors = Object.entries(data)
    .filter(([, msgs]) => Array.isArray(msgs) && msgs.length)
    .map(([field, msgs]) => `${field}: ${msgs.join(" ")}`);

  return fieldErrors.length ? fieldErrors.join(" | ") : fallback;
};

const DRIVER_FIELDS_NOTE = "Filled in automatically from the assigned driver.";

const MAX_FILENAME_LENGTH = 100;

const shortenFileName = (file, maxLength = MAX_FILENAME_LENGTH) => {
  if (file.name.length <= maxLength) return file;

  const dotIndex = file.name.lastIndexOf(".");
  const ext = dotIndex !== -1 ? file.name.slice(dotIndex) : "";
  const base = dotIndex !== -1 ? file.name.slice(0, dotIndex) : file.name;
  const truncatedBase = base.slice(0, Math.max(1, maxLength - ext.length));

  return new File([file], `${truncatedBase}${ext}`, { type: file.type });
};

const AddVehicle = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const watchedTrackingStatus = Form.useWatch("trackingStatus", form);
  const [navixyKeyOptions, setNavixyKeyOptions] = useState([]);
  const [navixyKeysLoading, setNavixyKeysLoading] = useState(false);
  const [trackerOptions, setTrackerOptions] = useState([]);
  const [trackersLoading, setTrackersLoading] = useState(false);
  const watchedNavixyKey = Form.useWatch("navixyApiKey", form);

  const [odometerNote, setOdometerNote] = useState(null);

  const fetchNavixyOdometer = async (apiKey, trackerId) => {
    if (!apiKey || !trackerId) {
      setOdometerNote(null);
      return;
    }
    setOdometerNote({ type: "loading", text: "Fetching reading from Navixy..." });
    try {
      const res = await getNavixyOdometer(apiKey, trackerId);
      const reading = res?.data?.data?.odometer;
      if (Math.floor(res?.status / 100) === 2 && reading != null) {
        form.setFieldsValue({ odometer: String(reading) });
        form.setFields([{ name: "odometer", errors: [] }]);
        setOdometerNote({
          type: "success",
          text: "Fetched from Navixy. You can change it if needed.",
        });
      } else {
        setOdometerNote({
          type: "warning",
          text:
            res?.data?.message ||
            "Navixy has no odometer reading for this tracker yet. Please enter it manually.",
        });
      }
    } catch (err) {
      setOdometerNote({
        type: "warning",
        text:
          err?.response?.data?.message ||
          "Couldn't fetch the reading from Navixy. Please enter it manually.",
      });
    }
  };

  const [avatar, setAvatar] = useState(null);
  const [avatarFile, setAvatarFile] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [driverOptions, setDriverOptions] = useState([]);
  const [driverDetails, setDriverDetails] = useState({});
  const [fuelTypeOptions, setFuelTypeOptions] = useState([]);
  const [documentTypeOptions, setDocumentTypeOptions] = useState([]);
  const [vehicleTypeOptions, setVehicleTypeOptions] = useState([]);
  const [fuelCardOptions, setFuelCardOptions] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    const checkVehicleSlots = async () => {
      try {
        const res = await getVehicles();
        const data = res?.data?.data;
        const remaining = data?.vehicle_slots_remaining ?? data?.vehicle_limit;

        if (
          Math.floor(res?.status / 100) === 2 &&
          remaining !== undefined &&
          remaining !== null &&
          Number(remaining) <= 0
        ) {
          navigate("/vehicles-manager", {
            replace: true,
            state: { showLimitModal: true },
          });
        }
      } catch (err) {
        console.error("Failed to load vehicle limit:", err);
      }
    };

    checkVehicleSlots();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [creditData, setCreditData] = useState({
    activeFuelCard: false,
    fuelCardId: null,
    dailyLimit: null,
    dailyLimitEnabled: false,
    monthlyLimit: null,
    monthlyLimitEnabled: false,
    weeklyLimit: null,
    weeklyLimitEnabled: false,
    weekendLimit: null,
    weekendRulesEnabled: false,
    allowAnyDriver: false,
    autoReset: false,
  });

  useEffect(() => {
    const fetchDrivers = async () => {
      try {
        const res = await getDriversLookup();
        const list = Array.isArray(res?.data)
          ? res.data
          : res?.data?.data || res?.data?.results || [];

        if (Math.floor(res?.status / 100) === 2 && Array.isArray(list)) {
          setDriverOptions(list.map((d) => ({ label: d.name, value: d.id })));
          setDriverDetails(Object.fromEntries(list.map((d) => [d.id, d])));
        }
      } catch (err) {
        console.error("Failed to load drivers:", err);
      }
    };

    fetchDrivers();
  }, []);

  const watchedDriver = Form.useWatch("driver", form);

  useEffect(() => {
    const driver = driverDetails[watchedDriver];
    if (driver) {
      form.setFieldsValue({
        jobTitle: driver.job_title,
        address: driver.address,
      });
    }
  }, [watchedDriver, driverDetails, form]);

  useEffect(() => {
    const fetchFuelTypes = async () => {
      try {
        const res = await getFuelTypes();
        const list = Array.isArray(res?.data)
          ? res.data
          : res?.data?.data || res?.data?.results || [];

        if (Math.floor(res?.status / 100) === 2 && Array.isArray(list)) {
          setFuelTypeOptions(list.map((f) => ({ label: f.name, value: f.id })));
        }
      } catch (err) {
        console.error("Failed to load fuel types:", err);
      }
    };

    fetchFuelTypes();
  }, []);

  useEffect(() => {
    if (watchedTrackingStatus !== "linked" || navixyKeyOptions.length) return;

    const fetchNavixyKeys = async () => {
      setNavixyKeysLoading(true);
      try {
        const res = await getNavixyApiKeys();
        if (Math.floor(res?.status / 100) === 2) {
          const options = toNavixyKeyOptions(res);
          setNavixyKeyOptions(options);
          if (options.length && !form.getFieldValue("navixyApiKey")) {
            form.setFieldsValue({ navixyApiKey: options[0].value });
          }
        }
      } catch (err) {
        message.error(
          getErrorMessage(err?.response?.data, "Failed to load Navixy API keys."),
        );
      } finally {
        setNavixyKeysLoading(false);
      }
    };

    fetchNavixyKeys();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedTrackingStatus]);

  useEffect(() => {
    if (!watchedNavixyKey) {
      setTrackerOptions([]);
      return;
    }

    let cancelled = false;
    const fetchTrackers = async () => {
      setTrackersLoading(true);
      try {
        const res = await getNavixyTrackers(watchedNavixyKey);
        if (!cancelled && Math.floor(res?.status / 100) === 2) {
          setTrackerOptions(toNavixyTrackerOptions(res));
        }
      } catch (err) {
        if (!cancelled) {
          setTrackerOptions([]);
          message.error(
            getErrorMessage(err?.response?.data, "Failed to load Navixy trackers."),
          );
        }
      } finally {
        if (!cancelled) setTrackersLoading(false);
      }
    };

    fetchTrackers();
    return () => {
      cancelled = true;
    };
  }, [watchedNavixyKey]);

  useEffect(() => {
    const fetchVehicleTypes = async () => {
      try {
        const res = await getVehicleTypes();
        const list = res?.data?.data || [];

        if (Math.floor(res?.status / 100) === 2 && Array.isArray(list)) {
          setVehicleTypeOptions(
            list.map((t) => ({ value: t.value, label: t.label })),
          );
        }
      } catch (err) {
        console.error("Failed to load vehicle types:", err);
      }
    };

    fetchVehicleTypes();
  }, []);

  useEffect(() => {
    const fetchDocumentTypes = async () => {
      try {
        const res = await getVehicleDocumentTypes();
        const list = res?.data?.data || [];

        if (Math.floor(res?.status / 100) === 2 && Array.isArray(list)) {
          setDocumentTypeOptions(
            list.map((t) => ({
              value: t.document_name,
              label: t.document_name,
              allowedFormats: t.allowed_formats || [],
            })),
          );
        }
      } catch (err) {
        console.error("Failed to load document types:", err);
      }
    };

    fetchDocumentTypes();
  }, []);

  useEffect(() => {
    const fetchFuelCards = async () => {
      try {
        const res = await getAvailableFuelCards();
        const card = res?.data?.data;

        if (Math.floor(res?.status / 100) === 2 && card?.id) {
          setFuelCardOptions([{ label: card.card_number, value: card.id }]);
          setCreditData((prev) => ({ ...prev, fuelCardId: card.id }));
        }
      } catch (err) {
        console.error("Failed to load available fuel cards:", err);
      }
    };

    fetchFuelCards();
  }, []);

  const fileInputRef = useRef(null);

  const handleButtonClick = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const shortened = shortenFileName(file);
      setAvatarFile(shortened);

      const reader = new FileReader();
      reader.onload = () => {
        setAvatar(reader.result);
      };
      reader.readAsDataURL(shortened);
    }
  };

  const handleCreditChange = (key, value) => {
    setCreditData((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async () => {
    if (!creditData.fuelCardId) {
      message.error("No fuel card is available to assign to this vehicle.");
      return;
    }

    let values;
    try {
      values = await form.validateFields();
    } catch (error) {
      message.error("Please fill in all required fields.");
      return;
    }

    const formData = new FormData();
    formData.append("make", values.make ?? "");
    formData.append("model", values.model ?? "");
    formData.append("year", values.year ?? "");
    formData.append("vin", values.vin ?? "");
    formData.append("license_plate", values.licensePlate ?? "");
    formData.append("vehicle_type", values.vehicleType ?? "");
    formData.append("tracking_status", values.trackingStatus ?? "");
    if (values.trackingStatus === "linked") {
      formData.append("navixy_api_key", values.navixyApiKey ?? "");
      formData.append("navixy_tracker_id", values.navixyTrackerId ?? "");
    }
    formData.append("fuel_sensor_status", values.fuelStatus ?? "");
    formData.append("odometer_reading", values.odometer ?? "");
    formData.append("tank_capacity", values.tankcapacity ?? "");
    formData.append("fuel_economy", values.fueleconomy ?? "");
    if (typeof values.fuelType === "number") {
      formData.append("fuel_type", values.fuelType);
    }
    if (typeof values.driver === "number") {
      formData.append("driver", values.driver);
    }
    if (avatarFile) {
      formData.append("avatar", avatarFile);
    }
    formData.append("fuel_card_id", creditData.fuelCardId);
    formData.append("daily_limit", creditData.dailyLimit ?? "");
    formData.append("daily_limit_enabled", creditData.dailyLimitEnabled);
    formData.append("weekly_limit", creditData.weeklyLimit ?? "");
    formData.append("weekly_limit_enabled", creditData.weeklyLimitEnabled);
    formData.append("monthly_limit", creditData.monthlyLimit ?? "");
    formData.append("monthly_limit_enabled", creditData.monthlyLimitEnabled);
    formData.append("weekend_limit", creditData.weekendLimit ?? "");
    formData.append("weekend_limit_enabled", creditData.weekendRulesEnabled);
    formData.append("allow_any_driver", creditData.allowAnyDriver);
    formData.append("auto_reset", creditData.autoReset);

    const newDocuments = documents.filter((doc) => doc.file);
    newDocuments.forEach((doc) => {
      formData.append(doc.category, doc.file);
    });
    if (newDocuments.length > 0) {
      const expiryDates = {};
      newDocuments.forEach((doc) => {
        expiryDates[doc.category] = doc.expiryDate || null;
      });
      formData.append("document_expiry_dates", JSON.stringify(expiryDates));
    }

    setSaving(true);
    try {
      const res = await createVehicle(formData);

      if (Math.floor(res?.status / 100) === 2) {
        message.success(
          getErrorMessage(res?.data, "Vehicle added successfully!"),
        );
        navigate(-1);
      } else {
        message.error(getErrorMessage(res?.data, "Failed to add vehicle."));
      }
    } catch (err) {
      message.error(
        getErrorMessage(err?.response?.data, "Failed to add vehicle."),
      );
    } finally {
      setSaving(false);
    }
  };

  const vehicleInformation = [
    {
      name: "make",
      label: "Make",
      type: "text",
      placeholder: "Enter Vehicle Make",
      rules: [{ required: true }],
    },
    {
      name: "model",
      label: "Model",
      type: "text",
      placeholder: "Enter Model",
      rules: [{ required: true }],
    },
    {
      name: "year",
      label: "Year",
      type: "number",
      placeholder: "Enter Year",
      rules: [{ required: true }],
    },
    {
      name: "vin",
      label: "VIN",
      type: "text",
      placeholder: "17-digit Chassis No.",
      rules: [
        { required: true },
        { max: 17, message: "VIN must be at most 17 characters" },
      ],
    },
    {
      name: "licensePlate",
      label: "License Plate",
      type: "text",
      placeholder: "Enter License Plate",
      rules: [{ required: true }],
    },
    {
      name: "vehicleType",
      label: "Vehicle Type",
      type: "select",
      placeholder: "Select Vehicle Type",
      options: vehicleTypeOptions,
      rules: [{ required: true }],
    },
    {
      name: "trackingStatus",
      label: "Tracking System Status",
      description: "Real-time GPS Monitoring",
      type: "segmented",
      options: [
        { label: "Not Linked", value: "not_linked" },
        { label: "Linked", value: "linked" },
      ],
    },
    ...(watchedTrackingStatus === "linked"
      ? [
          {
            name: "navixyApiKey",
            label: "Navixy API Key",
            type: "select",
            placeholder: "Select Navixy API Key",
            options: navixyKeyOptions,
            loading: navixyKeysLoading,
            showSearch: true,
            notFoundContent: "No Navixy API keys saved for your company",
            rules: [{ required: true, message: "Navixy API Key is required" }],
          },
          {
            name: "navixyTrackerId",
            label: "Navixy Tracker ID",
            type: "select",
            placeholder: watchedNavixyKey
              ? "Select Navixy Tracker"
              : "Select an API key first",
            options: trackerOptions,
            loading: trackersLoading,
            showSearch: true,
            disabled: !watchedNavixyKey,
            notFoundContent: "No trackers found for this API key",
            rules: [
              { required: true, message: "Navixy Tracker ID is required" },
            ],
          },
        ]
      : []),
    {
      name: "fuelStatus",
      label: "Fuel Sensor Status",
      description: "High Precision Level Sensor",
      type: "segmented",
      options: [
        { label: "None", value: "none" },
        { label: "Active", value: "active" },
      ],
    },
    {
      name: "odometer",
      label: "Current Odometer Reading (KM)",
      type: "text",
      placeholder: "Enter Current Odometer Reading (KM)",
      extra: odometerNote && (
        <span className={`field-note field-note--${odometerNote.type}`}>
          {odometerNote.text}
        </span>
      ),
      rules: [{ required: true }],
    },
  ];

  const specsInfoFields = [
    {
      name: "fuelType",
      label: "Fuel Type",
      type: "select",
      options: fuelTypeOptions,
      placeholder: "Select Fuel Type",
      rules: [{ required: true, message: "Fuel type is required" }],
    },
    {
      name: "tankcapacity",
      label: "Tank Capacity (Liter)",
      type: "number",
      placeholder: "Enter capacity",
      rules: [{ required: true }],
    },
    {
      name: "fueleconomy",
      label: "Fuel Economy (L/100)",
      type: "number",
      placeholder: "Enter Fuel Economy ",
      rules: [{ required: true }],
    },
  ];

  const operationsInformation = [
    {
      name: "driver",
      label: "Assigned Driver",
      type: "select",
      options: driverOptions,
      placeholder: "Select Assigned Driver",
    },
    {
      name: "jobTitle",
      label: "Job Title",
      type: "text",
      placeholder: "Auto-filled from the selected driver",
      disabled: true,
      extra: DRIVER_FIELDS_NOTE,
    },
    {
      name: "address",
      label: "Address",
      type: "text",
      placeholder: "Auto-filled from the selected driver",
      disabled: true,
      extra: DRIVER_FIELDS_NOTE,
    },
  ];

  const limitGridConfig = [
    {
      label: "Daily Spending Limit",
      sub: "Resets at 00:00 UTC",
      key: "dailyLimit",
      toggle: "dailyLimitEnabled",
    },
    {
      label: "Monthly Spending Limit",
      sub: "Resets 1st of month",
      key: "monthlyLimit",
      toggle: "monthlyLimitEnabled",
    },
    {
      label: "Weekly Spending Limit",
      sub: "Resets every Monday",
      key: "weeklyLimit",
      toggle: "weeklyLimitEnabled",
    },
    {
      label: "Weekend Rules",
      sub: "Restricted Sat/Sun",
      key: "weekendLimit",
      toggle: "weekendRulesEnabled",
      accent: "green-accent",
    },
  ];

  const handleCancel = () => {
    form.resetFields();
    setAvatar(null);
    setAvatarFile(null);
    setDocuments([]);
    setCreditData({
      activeFuelCard: false,
      fuelCardId: fuelCardOptions[0]?.value ?? null,
      dailyLimit: null,
      dailyLimitEnabled: false,
      monthlyLimit: null,
      monthlyLimitEnabled: false,
      weeklyLimit: null,
      weeklyLimitEnabled: false,
      weekendLimit: null,
      weekendRulesEnabled: false,
      allowAnyDriver: false,
      autoReset: false,
    });
  };

  return (
    <div className="edit-company vehicle-detail">
      <div className="back-btn">
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
          Back
        </Button>
      </div>

      <div className="header-wrapper">
        <div className="title">Add New Vehicle</div>
      </div>

      <Form
        form={form}
        layout="vertical"
        onValuesChange={(changed) => {
          if ("navixyApiKey" in changed) {
            form.setFieldsValue({ navixyTrackerId: undefined });
            setOdometerNote(null);
          }
          if ("navixyTrackerId" in changed) {
            fetchNavixyOdometer(
              form.getFieldValue("navixyApiKey"),
              changed.navixyTrackerId,
            );
          }
        }}
      >
        <div className="row">
          <div className="profile-card">
            <div className="profile-header">
              <div className="btn">
                <Button
                  type="default"
                  className="upload-btn"
                  onClick={handleButtonClick}
                >
                  Upload Photo
                </Button>
              </div>
              <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                style={{ display: "none" }}
                onChange={handleFileChange}
              />

              <div className="profile-avatar">
                {avatar ? (
                  <img
                    src={avatar}
                    alt="avatar"
                    style={{
                      width: "100%",
                      height: "100%",
                      borderRadius: "50%",
                    }}
                  />
                ) : (
                  "T"
                )}
              </div>
            </div>
            <div className="profile-body"></div>
          </div>
        </div>

        <InputCard title="Vehicle Information" fields={vehicleInformation} />
        <InputCard title="Technical Specs" fields={specsInfoFields} />

        <div className="credit-controls">
          <div className="header-section">
            <div className="heading">Credit Controls</div>
          </div>

          <div className="control-row">
            <div
              className={`status-card ${creditData.activeFuelCard ? "active-card" : ""}`}
            >
              <div className="icon-box">
                <img src={cardIcon} alt="card Icon" />
              </div>
              <div className="wrapper">
                <div className="card-info">
                  <div className="label-main">Active Fuel Card</div>
                  <div className="label-sub">
                    Enable instant digital wallet payments
                  </div>
                </div>
                <div>
                  <Switch
                    checked={creditData.activeFuelCard}
                    onChange={(val) =>
                      handleCreditChange("activeFuelCard", val)
                    }
                  />
                </div>
              </div>
            </div>

            <div className="field-display">
              <div className="field-label">
                Fuel Card <span className="required">*</span>
              </div>
              <Input
                className="field-box"
                style={{ width: "100%" }}
                placeholder="No fuel card available"
                variant="borderless"
                value={fuelCardOptions[0]?.label || ""}
                readOnly
                disabled
              />
            </div>
          </div>

          <div className="limits-grid">
            {limitGridConfig.map((item) => (
              <div className={`limit-card ${item.accent || ""}`} key={item.key}>
                <div className="limit-header">
                  <div>
                    <div className="limit-title">{item.label}</div>
                    <div className="limit-reset">{item.sub}</div>
                  </div>
                  <Switch
                    checked={creditData[item.toggle]}
                    onChange={(val) => handleCreditChange(item.toggle, val)}
                  />
                </div>
                <div className="value-box">
                  <span className="currency">$</span>
                  <InputNumber
                    className="w-full"
                    variant="borderless"
                    controls={false}
                    min={0}
                    placeholder="0.00"
                    disabled={!creditData[item.toggle]}
                    value={creditData[item.key]}
                    onChange={(val) => handleCreditChange(item.key, val)}
                  />
                </div>
              </div>
            ))}
          </div>

          <div className="advanced-section">
            <div className="section-tag">ADVANCED RULE LOGIC</div>
            <div className="logic-row">
              <div className="logic-item">
                <div className="logic-content">
                  <div className="logic-icon">
                    <img src={refuelIcon} alt="refuel" />
                  </div>
                  <span>Allow Any Authorized Driver to Refuel</span>
                </div>
                <Switch
                  checked={creditData.allowAnyDriver}
                  onChange={(val) => handleCreditChange("allowAnyDriver", val)}
                />
              </div>
              <div className="logic-item">
                <div className="logic-content">
                  <div className="logic-icon">
                    <img src={resetIcon} alt="reset" />
                  </div>
                  <span>Auto Reset Limits</span>
                </div>
                <Switch
                  checked={creditData.autoReset}
                  onChange={(val) => handleCreditChange("autoReset", val)}
                />
              </div>
            </div>
          </div>
        </div>

        <InputCard title="Operations" fields={operationsInformation} />

        <GenericDocumentManagerTable
          initialDocuments={documents}
          setDocuments={setDocuments}
          documentTypes={documentTypeOptions}
          allowSelectAll={true}
        />

        <div className="btns-row">
          <Button onClick={handleCancel} disabled={saving}>
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={handleSubmit}
            className="save-btn"
            loading={saving}
            disabled={saving}
          >
            Add Vehicle
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default AddVehicle;
